import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import { emailLogsService } from "@services/apis/EmailLogs";
import {
  EMAIL_LOG_REFERENCE_TYPES,
  EMAIL_LOG_STATUSES,
  EmailLog,
  EmailLogStatsSummary,
} from "@services/models/EmailLogs/EmailLogsModel";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ConstantFunctions from "../../utils/constants_functions";
import EmailLogDetails from "./components/EmailLogDetails";
import {
  EmailLogStatusPillRenderer,
  getEmailLogStatusColors,
} from "./components/EmailLogStatusPill";

const PAGE_LIMIT = 50;
const constantFunctions = new ConstantFunctions();

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";
  try {
    return constantFunctions.formatDateTime(value);
  } catch {
    return value;
  }
};

const EmailLogs = () => {
  const gridRef = useRef<any>(null);
  const inFlight = useRef(false);

  const [rows, setRows] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [referenceTypeFilter, setReferenceTypeFilter] = useState<
    string | undefined
  >();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [stats, setStats] = useState<EmailLogStatsSummary | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [selectedEmailLogId, setSelectedEmailLogId] = useState<string | null>(
    null,
  );
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);

  const listFilters = useMemo(
    () => ({
      search: appliedSearchTerm || undefined,
      status: statusFilter,
      referenceType: referenceTypeFilter,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    }),
    [appliedSearchTerm, fromDate, referenceTypeFilter, statusFilter, toDate],
  );

  const fetchLogs = useCallback(
    async (pageNum: number, reset: boolean) => {
      if (inFlight.current) return;
      inFlight.current = true;

      try {
        setLoading(true);
        setError("");

        const res = await emailLogsService.listLogs({
          page: pageNum,
          limit: PAGE_LIMIT,
          sortBy: "createdAt",
          sortOrder: "DESC",
          ...listFilters,
        });

        const logs = res.data?.logs ?? [];
        const pagination = res.data?.pagination;
        const totalPages = pagination?.totalPages ?? 1;

        setTotalRecords(pagination?.totalRecords ?? logs.length);
        setHasMore(pageNum < totalPages);

        if (reset) {
          setRows(logs);
        } else {
          setRows((prev) => [...prev, ...logs]);
        }
      } catch (err: any) {
        if (reset) setRows([]);
        setHasMore(false);
        setError(err?.response?.data?.msg || "Failed to fetch email logs");
      } finally {
        setLoading(false);
        inFlight.current = false;
      }
    },
    [listFilters],
  );

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const res = await emailLogsService.getStats();
      setStats(res.data ?? null);
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchLogs(1, true);
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleSearch = useCallback(() => {
    setAppliedSearchTerm(searchTerm.trim());
  }, [searchTerm]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setAppliedSearchTerm("");
  }, []);

  const toggleFilter = (
    current: string | undefined,
    next: string,
    setter: (value: string | undefined) => void,
  ) => {
    setter(current === next ? undefined : next);
  };

  const hasActiveFilters =
    Boolean(appliedSearchTerm) ||
    Boolean(statusFilter) ||
    Boolean(referenceTypeFilter) ||
    Boolean(fromDate) ||
    Boolean(toDate);

  const clearFilters = () => {
    setSearchTerm("");
    setAppliedSearchTerm("");
    setStatusFilter(undefined);
    setReferenceTypeFilter(undefined);
    setFromDate("");
    setToDate("");
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Created At",
        field: "createdAt",
        minWidth: 150,
        flex: 1,
        valueFormatter: (params) => formatDateTime(params.value),
      },
      {
        headerName: "Recipient",
        field: "recipient",
        minWidth: 180,
        flex: 1.2,
        cellRenderer: ({ data }: { data: EmailLog }) => {
          if (!data?.recipient) return "—";
          return (
            <div className="flex h-full items-center justify-between gap-1">
              <span className="truncate">{data.recipient}</span>
              <CopyButton
                copyText={data.recipient}
                copyMessage="Recipient copied!"
                copyIconSize={10}
                buttonSize="14px"
                borderRadius="2px"
              />
            </div>
          );
        },
      },
      {
        headerName: "Subject",
        field: "subject",
        minWidth: 150,
        maxWidth: 300,
        flex: 1,
        cellRenderer: ({ data }: { data: EmailLog }) => (
          <span className="line-clamp-2 break-words leading-tight">
            {data?.subject || "—"}
          </span>
        ),
      },
      {
        headerName: "Template",
        field: "templateName",
        minWidth: 220,
        flex: 1.1,
        valueFormatter: (params) => params.value || "—",
      },
      {
        headerName: "Status",
        field: "status",
        minWidth: 110,
        maxWidth: 140,
        cellRenderer: EmailLogStatusPillRenderer,
      },
      {
        headerName: "From",
        field: "fromEmail",
        minWidth: 150,
        flex: 1,
        valueFormatter: (params) => params.value || "—",
      },
      {
        headerName: "Reference",
        field: "referenceType",
        minWidth: 150,
        maxWidth: 200,
        flex: 1,
        cellRenderer: ({ data }: { data: EmailLog }) => {
          const type = data?.referenceType;
          const id = data?.referenceId;
          if (!type && !id) return "—";
          return (
            <div className="flex h-full flex-col justify-center leading-tight">
              {type && <span className="truncate">{type}</span>}
              {id && <span className="truncate text-text-subtle">{id}</span>}
            </div>
          );
        },
      },
      {
        headerName: "Sent At",
        field: "sentAt",
        minWidth: 150,
        flex: 1,
        valueFormatter: (params) => formatDateTime(params.value),
      },
      {
        headerName: "Delivered At",
        field: "deliveredAt",
        minWidth: 150,
        flex: 1,
        valueFormatter: (params) => formatDateTime(params.value),
      },
    ],
    [],
  );

  const onRowClicked = (event: RowClickedEvent<EmailLog>) => {
    const selectedData = event.data;

    const allRows = document.querySelectorAll(".ag-row");
    allRows.forEach((row) => {
      row.classList.remove("ag-row-selected-custom");
    });

    if (event.rowIndex !== null) {
      const rowElement = document.querySelector(
        `[row-index="${event.rowIndex}"]`,
      );
      if (rowElement) {
        rowElement.classList.add("ag-row-selected-custom");
      }
    }

    if (!selectedData?.emailLogId) return;
    setSelectedEmailLogId(selectedData.emailLogId);
    setIsPanelCollapsed(false);
  };

  const loadMoreData = useCallback(() => {
    if (inFlight.current || loading || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchLogs(nextPage, false);
  }, [fetchLogs, hasMore, loading, page]);

  const onBodyScroll = useCallback(
    (params: any) => {
      if (!hasMore || loading) return;

      const api = params.api;
      const displayedRowCount = api.getDisplayedRowCount();
      const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

      if (
        lastDisplayedRowIndex >= displayedRowCount - 5 &&
        displayedRowCount > 0
      ) {
        loadMoreData();
      }
    },
    [hasMore, loadMoreData, loading],
  );

  const statusCounts = stats?.statusCounts ?? {};
  const statCards = [
    { label: "Total", value: stats?.total ?? 0, status: undefined as string | undefined },
    ...EMAIL_LOG_STATUSES.map((status) => ({
      label: status.replace("_", " "),
      value: statusCounts[status] ?? 0,
      status: status as string | undefined,
    })),
  ];

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <div className="sticky top-0 z-10 flex h-[70px] shrink-0 items-center justify-between gap-4 border-b border-border-subtle bg-bg-page-body px-[20px]">
        <p className="shrink-0 text-[14px] font-semibold text-text-base">
          Email Logs
        </p>
        <div className="w-[360px] shrink-0">
          <GlobalSearchInputField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={handleSearch}
            onClear={handleClearSearch}
            disabled={loading}
            isSearchApplied={Boolean(appliedSearchTerm)}
            placeholder="Search recipient, subject, template, IDs..."
          />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-[12px] overflow-hidden bg-bg-page-body px-[16px] pt-[16px]">
        <div className="flex shrink-0 gap-2 overflow-x-auto pb-1">
          {statCards.map((card) => {
            const colors = getEmailLogStatusColors(card.status);
            const isActive =
              card.status === undefined
                ? !statusFilter
                : statusFilter === card.status;
            return (
              <button
                key={card.label}
                type="button"
                onClick={() =>
                  setStatusFilter(
                    card.status === undefined
                      ? undefined
                      : statusFilter === card.status
                        ? undefined
                        : card.status,
                  )
                }
                className={`flex min-w-[92px] flex-col items-start rounded-lg border px-2 py-2 text-left ${
                  isActive
                    ? "border-border-button-secondary bg-bg-button-secondary"
                    : "border-border-subtle bg-bg-page-body"
                }`}
              >
                <span className="text-[9px] font-medium uppercase text-text-subtle">
                  {card.label}
                </span>
                <span
                  className="text-[14px] font-semibold"
                  style={card.status ? { color: colors.text } : undefined}
                >
                  {statsLoading ? "—" : card.value}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <span className="text-[10px] font-bold text-text-base">
            Reference:
          </span>
          {EMAIL_LOG_REFERENCE_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() =>
                toggleFilter(referenceTypeFilter, type, setReferenceTypeFilter)
              }
              className={`rounded-[100px] border px-[12px] py-[4px] text-[10px] font-medium text-text-base ${
                referenceTypeFilter === type
                  ? "border-border-button-secondary bg-bg-button-secondary"
                  : "border-border-subtle bg-bg-page-body"
              }`}
            >
              {type}
            </button>
          ))}

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1 text-[10px] text-text-subtle">
              From
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-[28px] rounded border border-border-subtle bg-transparent px-2 text-[10px] text-text-base"
              />
            </label>
            <label className="flex items-center gap-1 text-[10px] text-text-subtle">
              To
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-[28px] rounded border border-border-subtle bg-transparent px-2 text-[10px] text-text-base"
              />
            </label>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="rounded border border-border-subtle px-[10px] py-[4px] text-[10px] font-medium text-text-base"
              >
                Clear filters
              </button>
            )}
            <span className="text-[10px] text-text-subtle">
              {totalRecords} records
            </span>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-row items-stretch overflow-hidden">
          <div
            className={`flex h-full min-h-0 flex-col overflow-hidden bg-bg-page-body ${
              selectedEmailLogId && !isPanelCollapsed ? "w-3/4 pr-[12px]" : "w-full"
            }`}
          >
            {loading && page === 1 && rows.length === 0 ? (
              <AgGridSkeleton
                columnDefs={columnDefs}
                style={{ height: "100%", width: "100%" }}
              />
            ) : (
              <div className="flex h-full min-h-0 flex-col">
                <AgGridReactTable
                  ref={gridRef}
                  rowData={rows}
                  columnDefs={columnDefs}
                  onRowClicked={onRowClicked}
                  onBodyScroll={onBodyScroll}
                  error={error}
                  getRowClass={() => "cursor-pointer"}
                  getRowId={(params) =>
                    params.data?.emailLogId || String(params.data?.id)
                  }
                  style={{ height: "100%", width: "100%", minHeight: 0 }}
                />
                {loading && rows.length > 0 && (
                  <div className="flex items-center justify-center py-4 border-t border-border-subtle">
                    <div className="flex items-center space-x-2 text-text-subtle">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-border-subtle border-t-border-button-primary"></div>
                      <span className="text-sm">Loading more logs...</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {selectedEmailLogId && (
            <div
              className={`relative h-full min-h-0 self-stretch border-l border-border-subtle bg-bg-page-body transition-all duration-300 ${
                isPanelCollapsed ? "w-0 px-0" : "w-1/4 px-2"
              }`}
            >
              <button
                onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
                className="absolute top-1/2 -translate-y-1/2 z-20 bg-bg-button-secondary text-text-base rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center group"
                style={{
                  left: isPanelCollapsed ? "calc(100% - 32px)" : "-16px",
                  width: "32px",
                  height: "60px",
                }}
                aria-label={
                  isPanelCollapsed ? "Expand panel" : "Collapse panel"
                }
              >
                <span className="material-symbols-outlined text-[20px] group-hover:scale-110 transition-transform">
                  {isPanelCollapsed ? "chevron_left" : "chevron_right"}
                </span>
              </button>

              <div
                className={`h-full overflow-auto transition-opacity duration-300 ${
                  isPanelCollapsed
                    ? "opacity-0 pointer-events-none"
                    : "opacity-100"
                }`}
              >
                <EmailLogDetails emailLogId={selectedEmailLogId} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailLogs;
